/* eslint-disable max-lines */

import React, { Component } from 'react'
import cx from 'classnames'
import PropTypes from 'prop-types'

import { CloseIcon } from '@wf-sl/common-icons'

import { getExtraneousProps } from '../../util/react'
import T3Constants from '../constants'
import { ErrorPopover } from '../popover/error-popover'
import Cursor from './cursor'
import TextFieldIcon from './textfield-icon'

import styles from './textfield.module.css'

/**
 * Hint: If propTypes are updated please check that you also update
 * SearchField accordingly, which is nearly the same as TextField
 * because it's just a wrapper which passes props to TextField.
 */
const propTypes = {
    autoComplete: PropTypes.string,
    className: PropTypes.string,
    containerClassName: PropTypes.string,
    containerStyle: PropTypes.object, // eslint-disable-line react/forbid-prop-types
    disabled: PropTypes.bool,
    // PropTypes.instanceOf(Error) won't work due to babel6
    error: PropTypes.oneOfType([
        PropTypes.bool,
        PropTypes.object // eslint-disable-line react/forbid-prop-types
    ]),
    full: PropTypes.bool,
    htmlElement: PropTypes.oneOf(['input', 'textarea']),
    initialValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    leftIcon: PropTypes.node,
    // @deprecated: Set className directly in leftIcon node instead
    leftIconClassName: PropTypes.string,
    // @deprecated: Set props directly in leftIcon n ode instead
    leftIconProps: PropTypes.object, // eslint-disable-line react/forbid-prop-types
    // @deprecated: Set style directly in leftIcon node instead
    leftIconStyle: PropTypes.object, // eslint-disable-line react/forbid-prop-types
    maxLength: PropTypes.number,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
    onFocus: PropTypes.func,
    onFormat: PropTypes.func,
    onKeyDown: PropTypes.func,
    placeholder: PropTypes.string,
    resetButton: PropTypes.bool,
    rightIcon: PropTypes.node,
    rightIconClassName: PropTypes.string, // @deprecated: Set className directly in rightIcon node instead
    // @deprecated: Set props directly in rightIcon node instead
    rightIconProps: PropTypes.object, // eslint-disable-line react/forbid-prop-types
    // @deprecated: Set style directly in rightIcon node instead
    rightIconStyle: PropTypes.object, // eslint-disable-line react/forbid-prop-types
    style: PropTypes.object, // eslint-disable-line react/forbid-prop-types
    type: PropTypes.oneOf(['text', 'password', 'number', 'search', 'email', 'tel', 'url', 'hidden']),
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
}

const defaultProps = {
    full: false,
    htmlElement: 'input',
    onBlur: Function.prototype,
    onChange: Function.prototype,
    onFocus: Function.prototype,
    onKeyDown: Function.prototype,
    resetButton: false,
    type: 'text'
}

/**
 * Helper function to avoid react console warnings. Looks a bit hacky, but:
 * - If the incoming value is <code>undefined</code> or <code>null</code>,
 *   then react won't render the html value attribute.
 * - The next time a valid value will be passed in (e.g. by state change/user
 *   input) react will complain about changing between un-/controlled component,
 *   even this component is a controlled one.
 * - To avoid this we make sure that we always store a valid value in state and
 *   set a value attribute.
 *
 * @function
 * @private
 * @param {*} value The passed props value
 * @returns {string|number} The valid state value or empty string
 */
function getStateValue(value) {
    if (value === undefined || value === null) {
        return ''
    }
    return value
}

/**
 * Check if given tagName is a textarea
 *
 * @function
 * @private
 * @param {string} tagName
 * @returns {boolean}
 */
function isTextarea(tagName) {
    return tagName === 'textarea'
}

/**
 * Check if given input has type number
 *
 * @function
 * @private
 * @param {string} type
 * @returns {boolean}
 */
function isNumberInput(type) {
    return type === 'number'
}

/**
 * To avoid w3c errors if the html element is a textarea
 * we add some required props (like cols and rows) to
 * the textarea...
 *
 * @function
 * @private
 * @param {string} HtmlElement The element type
 * @returns {Object|null} The textarea props or null
 */
function getTextareaRequiredProps(HtmlElement) {
    let textareaProps = null
    if (isTextarea(HtmlElement)) {
        textareaProps = { cols: '50', rows: '10' }
    }
    return textareaProps
}
/**
 * Some versions of safari have known issues if the input type
 * is number, so we add the pattern attribute here. If this is not
 * present safari will likely show nothing as the curent value....
 *
 * @function
 * @private
 * @param {string} type The current input type
 * @returns {Object|null} The pattern prop or null
 */
function getNumberRequiredProps(type) {
    let pattern = null
    if (isNumberInput(type)) {
        pattern = { pattern: '/^\\d*\\.?\\d*$/' }
    }
    return pattern
}

/**
 * Controlled form input component used as a base input for Form handler
 * and SearchInput.
 *
 * @example <caption>Default usage</caption>
 * <TextField placeholder='hello world' onChange={(value) => {filterFn(value)}} />
 * @example <caption>Control input value from parent component, value will update everytime the parent renders</caption>
 * <TextField value={value} onChange={(value) => {filterFn(value)}} />
 * @example <caption>Value will just be rendered on the first draw, controlled by TextFields internal state</caption>
 * <TextField initialValue={value} onChange={(value) => {filterFn(value)}} />
 *
 * @param {string} [props.className] - Additional css className for the input element
 * @param {Object} [props.style] - Additional css style for the input element
 * @param {string|number} [props.value] - Input value to be stored in internal state
 * @param {string|number} [props.initialValue] - Input value to be stored in internal state, just set in constructor
 * @param {string} [props.type='text'] - Input type (text, number, email, ....), just valid for inputs not for textarea
 * @param {string} [props.htmlElement='input'] - The html element to be rendered (input, textarea)
 * @param {number} [props.maxLength] - The maximum length of characters
 * @param {string} [props.placeholder] - The placeholder text for empty inputs
 * @param {Function} [props.onChange=noop] - Input value change callback
 * @param {Function|Object|boolean} [props.error] - ErrorPopover state
 * @param {string} [props.containerClassName] - Additional css className for the container element
 * @param {Object} [props.containerStyle] - Additional css style for the container element
 * @param {Object} [props.leftIcon] - Left icon component
 * @param {string} [props.leftIconClassName] - DEPRECATED: Additional css style for the left icon
 * @param {Object} [props.leftIconProps] - DEPRECATED: Additional props for the left icon
 * @param {Object} [props.leftIconStyle] - DEPRECATED: Additional css style for the left icon
 * @param {Object} [props.rightIcon] - Right icon component
 * @param {string} [props.rightIconClassName] - DEPRECATED: Additional css style for the right icon
 * @param {Object} [props.rightIconProps] - DEPRECATED: Additional props for the right icon
 * @param {Object} [props.rightIconStyle] - DEPRECATED: Additional css style for the right icon
 * @param {boolean} [props.resetButton=false] - Whether to show the reset button or not
 * @param {boolean} [props.full=false] - Whether to disable css max-width or not
 * @returns {ReactElement}
 */
class TextField extends Component {
    /**
     * @contructs
     * @param {Object} [props]
     * @returns {void}
     */
    constructor(props) {
        super(props)

        this.onChange = this.onChange.bind(this)
        this.onKeyDown = this.onKeyDown.bind(this)
        this.onKeyUp = this.onKeyUp.bind(this)
        this.onBlur = this.onBlur.bind(this)
        this.onReset = this.onReset.bind(this)
        this.focus = this.focus.bind(this)
        this.format = this.format.bind(this)
        this.formatLineBreak = this.formatLineBreak.bind(this)
        this.removeLineBreak = this.removeLineBreak.bind(this)

        this.state = {
            placeholder: props.placeholder,
            value: getStateValue(props.initialValue)
        }
        this.ctrlDown = false
    }

    /**
     * Just react if initialValue isn't set.
     *
     * @function
     * @returns {void}
     */
    UNSAFE_componentWillMount() {
        const { initialValue, value } = this.props
        if (initialValue !== undefined) {
            return
        }
        this.setState({ value: getStateValue(value) })
    }

    /**
     * Just react if initialValue isn't set.
     *
     * @function
     * @param {Object} nextProps
     * @returns {void}
     */
    UNSAFE_componentWillReceiveProps(nextProps) {
        const { initialValue, placeholder, value } = nextProps
        if (initialValue === undefined) {
            this.setState({ value: getStateValue(value) })
        }
        if (placeholder !== this.props.placeholder) {
            this.setState({ placeholder: placeholder })
        }
    }

    /**
     * @function
     * @returns {ReactElement}
     */
    render() {
        const {
            containerClassName,
            containerStyle,
            full,
            leftIcon,
            leftIconClassName,
            leftIconProps,
            leftIconStyle,
            resetButton,
            rightIcon,
            rightIconClassName,
            rightIconProps,
            rightIconStyle
        } = this.props

        const { value } = this.state
        const isValueEmpty = !value || !value.length
        const composedContainerClassName = cx(
            styles['container'],
            { [styles['full']]: full },
            't3sel-textfield',
            containerClassName
        )

        const leftIconRendered = (
            <TextFieldIcon
                icon={leftIcon}
                className={cx(styles['icon-left'], leftIconClassName)}
                style={leftIconStyle}
                {...leftIconProps}
            />
        )

        const rightIconFinalClassName = resetButton
            ? cx(styles['button-reset'], rightIconClassName, { [styles['button-reset-hidden']]: isValueEmpty })
            : cx(styles['icon-right'], rightIconClassName)

        const rightIconComponent = resetButton ? <CloseIcon onClick={this.onReset} /> : rightIcon

        const rightIconRendered = (
            <TextFieldIcon
                icon={rightIconComponent}
                className={rightIconFinalClassName}
                style={rightIconStyle}
                onClick={this.focus}
                {...rightIconProps}
            />
        )

        return (
            <div
                ref={ref => (this.containerRef = ref)}
                className={composedContainerClassName}
                onKeyDown={this.onKeyDown}
                onKeyUp={this.onKeyUp}
                style={containerStyle}
            >
                {leftIconRendered}
                {this.renderInput()}
                {rightIconRendered}
            </div>
        )
    }

    /**
     * Helper function to render input or textarea element.
     *
     * @function
     * @private
     * @returns {ReactElement}
     */
    renderInput() {
        const {
            autoComplete,
            className,
            disabled,
            error,
            htmlElement: HtmlElement,
            leftIcon,
            maxLength,
            onFocus,
            onFormat,
            resetButton,
            rightIcon,
            style
        } = this.props
        const { placeholder, value } = this.state
        const newValue = this.format(value)
        if (value !== newValue && onFormat) {
            setTimeout(() => this.setState({ value: newValue }), 1)
        }
        let { type } = this.props

        // filter out all props that are not allowed for an <input>-element (avoid https://fb.me/react-unknown-prop)
        const inputProps = getExtraneousProps(propTypes, this.props)

        const composedClassName = cx(
            't3sel-textfield-input',
            styles['textfield'],
            {
                [styles['error']]: error,
                [styles['textfield-icon-right']]: rightIcon || resetButton,
                [styles['textfield-icon-left']]: leftIcon,
                [styles['textfield-textarea']]: isTextarea(HtmlElement),
                't3sel-textfield-error': error
            },
            className
        )

        // If we render a textarea we have to add some required html attributes
        // and delete the type
        const textareaProps = getTextareaRequiredProps(HtmlElement)
        if (isTextarea(HtmlElement)) {
            type = null
        } else if (isNumberInput(type)) {
            type = 'text'
        }

        // Add pattern attribute for number inputs due to known safari issues
        const pattern = getNumberRequiredProps(type)

        return (
            <ErrorPopover error={error}>
                <HtmlElement
                    ref={ref => (this.inputRef = ref)}
                    onChange={this.onChange}
                    onBlur={this.onBlur}
                    onFocus={() => {
                        this.setState({ placeholder: null })
                        onFocus()
                    }}
                    className={composedClassName}
                    {...{ autoComplete, disabled, maxLength, placeholder, style, type, value }}
                    {...textareaProps}
                    {...pattern}
                    {...inputProps}
                />
            </ErrorPopover>
        )
    }

    onKeyDown(event) {
        const { onFormat, onKeyDown, resetButton } = this.props
        const keyCode = event.which || event.keyCode
        if (keyCode === T3Constants.KeyCodes.CTRL || keyCode === T3Constants.KeyCodes.CTRL2) {
            this.ctrlDown = true
        }
        if (keyCode === T3Constants.KeyCodes.ESCAPE && resetButton) {
            this.onReset(event)
        }
        if (keyCode === T3Constants.KeyCodes.Z && this.ctrlDown && onFormat) {
            // prevent undo operations when using formatters
            event.preventDefault()
        }
        onKeyDown(event)
    }

    onKeyUp(event) {
        const keyCode = event.which || event.keyCode
        if (keyCode === T3Constants.KeyCodes.CTRL || keyCode === T3Constants.KeyCodes.CTRL2) {
            this.ctrlDown = false
        }
    }

    onBlur(event) {
        const { onBlur, placeholder } = this.props
        const value = this.format(this.state.value, true)
        onBlur(event)
        this.setState({ placeholder, value })
    }

    /**
     * Handle change event.
     *
     * @function
     * @private
     * @param {SyntheticEvent} Reacts internal event object
     * @returns {void}
     */
    onChange(event) {
        const oldValue = this.state.value
        const { value } = event.target
        const { htmlElement: HtmlElement, onChange, onFormat } = this.props
        const newValue = this.format(value)

        // we need to persist here to allow references to the event, which is
        // needed for the Form handler onChange callback
        event.persist()
        this.setState({ value })
        if (onFormat) {
            // if there is a formatter that formats input in real-time while typing, take care of the cursor position
            const newCursorPosition = Cursor.findPosition(oldValue, newValue, this.inputRef)
            setTimeout(() => Cursor.setPosition(newCursorPosition, this.inputRef), 50)
        }
        if (isTextarea(HtmlElement)) {
            onChange(this.formatLineBreak(event))
        } else {
            onChange(this.removeLineBreak(event))
        }
    }

    /**
     * Handle reset event.
     *
     * @function
     * @private
     * @param {SyntheticEvent} Reacts internal event object
     * @returns {void}
     */
    onReset(event) {
        const { onChange } = this.props
        event.preventDefault()

        // we need to persist here to allow references to the event, which is
        // needed for the Form handler onChange callback
        event.persist()

        this.setState({ value: '' })
        onChange(event)
    }

    /**
     * Trigger focus on input dom node.
     *
     * @function
     * @returns {void}
     */
    focus() {
        this.getInputDOMNode().focus()
    }

    /**
     * Get dom node from container element. Just here for backwards compatibility.
     *
     * @function
     * @deprecated
     * @returns {Object} The dom node representation from the wrapper
     */
    getDOMNode() {
        return this.containerRef
    }

    /**
     * Get dom node from input element. Just here for backwards compatibility.
     *
     * @function
     * @deprecated
     * @returns {Object} The dom node representation from input element
     */
    getInputDOMNode() {
        return this.inputRef
    }

    /**
     * Retrieve current input value.
     *
     * @function
     * @returns {string|number} The current state value
     */
    getValue() {
        return this.state.value
    }

    /**
     * Set new input value.
     *
     * @function
     * @param {string|number} value The new state value
     * @returns {void}
     */
    setValue(value) {
        this.setState({ value })
    }

    format(value, finalize = false) {
        const { onFormat } = this.props
        return onFormat ? onFormat(value, finalize) : value
    }

    formatLineBreak(event) {
        if (event && event.target && event.target.value && typeof event.target.value === 'string') {
            event.target.value = event.target.value.replace(/\r?\n/g, '\r\n')
        }
        return event
    }

    removeLineBreak(event) {
        if (event && event.target && event.target.value && typeof event.target.value === 'string') {
            event.target.value = event.target.value.replace(/(\r|\n)+/g, ' ')
        }
        return event
    }
}

TextField.propTypes = propTypes
TextField.defaultProps = defaultProps

export default TextField
